<script setup lang="ts">
import { defineProps, defineEmits, ref, computed } from "vue";
import DiamondsSearchFormIcons from "@/components/diamonds/form/diamondsSearchFormIcons.vue";
import DiamondsSearchFormNumberRange from "@/components/diamonds/form/diamondsSearchFormNumberRange.vue";
import DiamondsSearchFormRange from "@/components/diamonds/form/diamondsSearchFormRange.vue";
import DiamondsSearchFormSwitch from "@/components/diamonds/form/diamondsSearchFormSwitch.vue";
import DiamondsSearchFormList from "@/components/diamonds/form/diamondsSearchFormList.vue";
import DiamondsSearchFormPresets from "@/components/diamonds/form/diamondsSearchFormPresets.vue";
import { getActiveValueLabelShared } from "@/assets/ts/diamondFilters";
import DiamondsSearchFormTextList from "@/components/diamonds/form/diamondsSearchFormTextList.vue";
import InfoModal from "@/components/info/infoModal.vue";
import {useI18n} from "vue-i18n";

const { t } = useI18n();

const props = withDefaults(defineProps<{
  filters: any;
  activeFilters: any;
  simpleFilterKeys?: string[];
  searchMode?: 'simple' | 'pro';
  presets?: any[];
}>(), {
  simpleFilterKeys: () => ['type', 'size', 'form'],
  searchMode: 'simple',
  presets: () => []
});

const indicatorRefs = ref<Record<string, any>>({});

defineExpose({
  indicatorRefs
});

const emit = defineEmits<{
  (e: "update:filters", value: any): void;
  (e: "updateFilters", value: any): void;
  (e: "update:searchMode", value: 'simple' | 'pro'): void;
}>();

const internalSearchMode = ref<'simple' | 'pro'>(props.searchMode);
const searchMode = computed({
  get: () => props.searchMode || internalSearchMode.value,
  set: (val: 'simple' | 'pro') => {
    internalSearchMode.value = val;
    emit('update:searchMode', val);
  }
});

const manualExpanded = ref<Record<string, boolean>>({});

const switchToSimple = () => {
  searchMode.value = 'simple';
  const newFilters = { ...props.activeFilters };
  const simpleKeys = props.simpleFilterKeys || ['type', 'size', 'form'];

  Object.keys(props.filters).forEach(key => {
    if (!simpleKeys.includes(key)) {
      newFilters[key] = props.filters[key].default !== undefined ? props.filters[key].default : null;
    }
  });

  emit("update:filters", newFilters);
};

const updateFilter = (key: string, value: any) => {
  const newFilters = { ...props.activeFilters };
  const filter = props.filters[key];

  let parsedValue = value;
  if (filter.type === 'bool') {
    if (value === 'true') parsedValue = true;
    else if (value === 'false') parsedValue = false;
  }

  const oldValue = newFilters[key];

  if (filter.type === 'text' && filter.view === 'list') {
    newFilters[key] = value;
  } else if (newFilters[key] === parsedValue) {
    newFilters[key] = filter.default !== undefined ? filter.default : null;
  } else {
    newFilters[key] = parsedValue;
  }

  // Special rule sets (copied from diamondsSearchForm.vue)
  if (key === 'type' && oldValue === 'earth' && newFilters.type === 'lab') {
    newFilters.institute = [];
    newFilters.color = ["E", "D"];
    newFilters.clarity = ["VS2", "VS1", "VVS2", "VVS1", "IF", "FL"];
  }

  if (key === 'type' && oldValue === 'lab' && newFilters.type === 'earth' && newFilters.colored === true) {
    newFilters.colored = false;
    newFilters.color = [];
    newFilters.fancy_intensity = [];
  }

  if (key === 'colored' && (oldValue === false || oldValue == null) && newFilters.colored === true) {
    newFilters.type = "lab";
    newFilters.clarity = [];
    newFilters.color = [];
    newFilters.cut = [];
    newFilters.polish = [];
    newFilters.fancy_intensity = [];
    newFilters.symmetry = [];
    newFilters.fluorescence = [];
    newFilters.institute = [];
    newFilters.fastDelivery = false;
  }

  if (key === 'form' && newFilters.form !== "BR") {
    newFilters.cut = [];
  }

  emit("update:filters", newFilters);
};

const sizeSteps = [0.3, 0.4, 0.5, 0.7, 1.0, 1.5, 2.0, 2.5, 3.0];
const filteredSizeSteps = computed(() => {
  const min = props.filters.size?.min ?? 0;
  const max = props.filters.size?.max ?? 10;
  return sizeSteps.filter(s => s >= min && s <= max);
});

const getDiamondImageUrl = (form: string) => {
  const formFilter = props.filters['form'];
  if (!formFilter || !formFilter.image) return "";
  const processedVal = formFilter.lowercaseImage !== false ? form.toLowerCase() : form;
  return formFilter.image.replace("{value}", processedVal);
};

const activeForm = computed(() => {
  return props.activeFilters['form'] || props.filters['form']?.default || 'BR';
});

const isFilterDefined = (key: string) => {
  const filter = props.filters[key];
  const active = props.activeFilters[key];
  if (!active) return false;
  // Handle objects (like carat or price range)
  if (typeof active === 'object' && filter.default && typeof filter.default === 'object') {
    return active.min !== filter.default.min || active.max !== filter.default.max;
  }
  if (Array.isArray(active) && Array.isArray(filter.default)) {
    return active.length !== filter.default.length || !active.every((v, i) => v === filter.default[i]);
  }
  if (filter.default && active === filter.default) return false;
  return true;
};

const isAlwaysVisible = (key: string) => {
  if (searchMode.value === 'simple') return true;
  return key === 'type';
}

const isFilterOpen = (key: string) => {
  if (isAlwaysVisible(key)) return true;
  if (isFilterDefined(key)) return true;
  if (manualExpanded.value[key] !== undefined) {
    return manualExpanded.value[key];
  }
  return props.filters[key]?.visible || false;
};

const toggleFilter = (key: string) => {
  if (isFilterDefined(key)) return;
  manualExpanded.value[key] = !isFilterOpen(key);
};

const sortedFilterKeys = computed(() => {
  const keys = Object.keys(props.filters)
    .filter(key => {
      if (key === 'sortOrder' || key === 'sort' || key === 'colored') return false;
      if (key === 'fancy_intensity' && !props.activeFilters.colored) return false;
      
      if (searchMode.value === 'simple') {
        return props.simpleFilterKeys?.includes(key);
      }
      
      return true;
    });

  return keys.sort((a, b) => {
    return (props.filters[a].order || 0) - (props.filters[b].order || 0);
  });
});

const getFilterLabel = (key: string) => {
  return t('components.diamonds.search.labels.' + key);
};

const getActiveValueLabel = (key: string, value: any) => {
  return getActiveValueLabelShared(key, value, props.filters, props.activeFilters);
};

</script>

<template>
  <div class="diamonds-mobile-search-form w-full px-4 py-6 bg-white border-b border-gray-100">
    <!-- Mode Switcher -->
    <div v-if="false" class="flex justify-center mb-10">
      <div class="relative bg-gray-100 p-1 rounded-xl flex w-full shadow-inner">
        <!-- Sliding Background -->
        <div
          class="absolute top-1 bottom-1 w-[calc(50%-4px)] bg-white rounded-lg shadow-sm transition-all duration-300 ease-out transform"
          :style="{ left: searchMode === 'pro' ? 'calc(50% + 2px)' : '4px' }"
        ></div>

        <!-- Buttons -->
        <button
          type="button"
          @click="switchToSimple"
          class="relative flex-1 flex items-center justify-center gap-2 py-2.5 text-sm font-semibold transition-colors duration-200 z-10"
          :class="searchMode === 'simple' ? 'text-gray-900' : 'text-gray-500 hover:text-gray-700'"
        >
          <svg xmlns="http://www.w3.org/2000/svg" class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
            <path stroke-linecap="round" stroke-linejoin="round" d="M4 6h16M4 12h16m-7 6h7" />
          </svg>
          Einfach
        </button>
        <button
          type="button"
          @click="searchMode = 'pro'"
          class="relative flex-1 flex items-center justify-center gap-2 py-2.5 text-sm font-semibold transition-colors duration-200 z-10"
          :class="searchMode === 'pro' ? 'text-gray-900' : 'text-gray-500 hover:text-gray-700'"
        >
          <svg xmlns="http://www.w3.org/2000/svg" class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
            <path stroke-linecap="round" stroke-linejoin="round" d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4" />
          </svg>
          Pro
        </button>
      </div>
    </div>

    <div v-for="key in sortedFilterKeys" :key="key" :ref="el => { if (el) indicatorRefs[key] = el }" class="mb-8 last:mb-0 scroll-mt-4">
      <button
        v-if="!isAlwaysVisible(key)"
        @click="toggleFilter(key)"
        class="w-full flex justify-between items-center py-2 mb-2 group"
      >
        <div class="flex gap-2 items-center">
          <h3 class="font-bold text-sm text-gray-900 tracking-tight uppercase">{{ getFilterLabel(key) }}:</h3>
          <span class="text-sm text-gray-500 font-light">{{ getActiveValueLabel(key, activeFilters[key]) }}</span>
          <info-modal :id="filters[key].modalId" :value="activeFilters[key]" />
        </div>
        <div class="flex items-center gap-2">
          <span v-if="isFilterDefined(key)" class="w-1.5 h-1.5 rounded-full bg-brand-main"></span>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            class="w-4 h-4 text-gray-400 transition-transform duration-300"
            :class="{ 'rotate-180': isFilterOpen(key) }"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
          </svg>
        </div>
      </button>

      <div v-else class="flex gap-2 items-center mb-4">
        <h3 class="font-bold text-sm text-gray-900 tracking-tight uppercase">{{ getFilterLabel(key) }}:</h3>
        <span class="text-sm text-gray-500 font-light">{{ getActiveValueLabel(key, activeFilters[key]) }}</span>
        <info-modal :id="filters[key].modalId" :value="activeFilters[key]" />
      </div>

      <div class="mobile-filter-content" v-show="isFilterOpen(key)">
        <template v-if="key === 'color' && filters.colored">
          <div class="mb-6">
            <DiamondsSearchFormSwitch
              :filter="filters.colored"
              :active-value="activeFilters.colored"
              @update="(val) => updateFilter('colored', val)"
            />
          </div>
        </template>

        <template v-if="key === 'color' && activeFilters.colored">
          <DiamondsSearchFormList
            class="mobile-list-grid"
            :filter="{...filters[key], values: filters[key].fancy_colors}"
            :active-value="activeFilters[key]"
            @update="(val) => updateFilter(key, val)"
          />
        </template>
        <template v-else-if="filters[key].type === 'icon' && filters[key].view === 'list'">
          <DiamondsSearchFormIcons
            class="mobile-list-grid"
            :filter="filters[key]"
            :active-value="activeFilters[key]"
            @update="(val) => updateFilter(key, val)"
          />
        </template>
        <template v-else-if="filters[key].type === 'number' && filters[key].view === 'range' && (key !== 'size' || searchMode === 'pro')">
          <DiamondsSearchFormNumberRange
            :filter="filters[key]"
            :active-value="activeFilters[key]"
            @update="(val) => updateFilter(key, val)"
          />
        </template>
        <template v-else-if="key === 'size' && searchMode === 'simple'">
          <div class="mt-6 overflow-x-auto no-scrollbar scroll-smooth">
            <div class="flex gap-8 pb-4 px-2 h-32">
              <div
                v-for="s in filteredSizeSteps"
                :key="s"
                class="flex flex-col justify-center items-center cursor-pointer min-w-fit"
                @click="updateFilter(key, {min: s, max: s})"
              >
                <div
                  class="w-16 h-16 overflow-hidden rounded-full flex items-center justify-center mb-2 transition-all duration-300 relative bg-white"
                  :class="activeFilters[key] && activeFilters[key].min === s && activeFilters[key].max === s
                    ? 'shadow-md w-20 h-20'
                    : ''"
                >
                  <div
                    class="transition-transform duration-500 ease-out"
                    :style="{ transform: `scale(${0.4 + (s / 3) * 0.6})` }"
                  >
                    <img
                      :src="getDiamondImageUrl(activeForm)"
                      :alt="activeForm"
                      class="w-18 h-18 object-contain opacity-80"
                    />
                  </div>
                </div>
                <span
                  class="text-sm text-gray-500 tracking-wide whitespace-nowrap transition-all duration-300"
                  :class="activeFilters[key] && activeFilters[key].min === s && activeFilters[key].max === s ? 'font-bold' : ''"
                >
                  {{ s.toLocaleString('de-DE', { minimumFractionDigits: 1 }) }} ct.
                </span>
              </div>
            </div>
          </div>
        </template>
        <template v-else-if="(filters[key].type === 'icon' || filters[key].type === 'text') && filters[key].view === 'range'">
          <DiamondsSearchFormRange
            :filter="filters[key]"
            :active-value="activeFilters[key]"
            @update="(val) => updateFilter(key, val)"
          />
        </template>
        <template v-else-if="(filters[key].type === 'text' || filters[key].type === 'bool') && filters[key].view === 'switch'">
          <DiamondsSearchFormSwitch
            :filter="filters[key]"
            :active-value="activeFilters[key]"
            @update="(val) => updateFilter(key, val)"
          />
        </template>
        <template v-else-if="filters[key].type === 'text' && filters[key].view === 'list'">
          <DiamondsSearchFormTextList
            class="mobile-list-grid"
            :filter="filters[key]"
            :active-value="activeFilters[key]"
            @update="(val) => updateFilter(key, val)"
          />
        </template>
      </div>

      <DiamondsSearchFormPresets
        v-if="key === 'type' && presets && presets.length > 0 && (typeof activeFilters.colored == 'undefined' || activeFilters.colored !== true)"
        :presets="presets"
        :active-filters="activeFilters"
        class="mt-6 mb-10"
        @update-filters="(val) => {
          emit('updateFilters', val);
          emit('update:filters', val);
        }"
      />
    </div>

    <!-- Bottom Filters (Fast Delivery and Sort) - Pro mode only -->
    <div v-if="false" class="mt-8 pt-8 border-t border-gray-200 flex flex-wrap items-center justify-between gap-6">
      <div class="flex items-center gap-3">
        <button 
          @click="updateFilter('fastDelivery', !activeFilters.fastDelivery)"
          class="relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none"
          :style="{ backgroundColor: activeFilters.fastDelivery ? '#a99d8b' : '#e5e7eb' }"
        >
          <span
            class="pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out"
            :class="activeFilters.fastDelivery ? 'translate-x-5' : 'translate-x-0'"
          />
        </button>
        <span class="text-[13px] uppercase tracking-widest text-[#4A5568]">Fast-Delivery</span>
      </div>

      <div class="flex items-center gap-3">
        <button 
          @click="updateFilter('sortOrder', activeFilters.sortOrder === 'asc' ? 'desc' : 'asc')"
          class="relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none"
          :style="{ backgroundColor: '#a99d8b' }"
        >
          <span
            class="pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out"
            :class="activeFilters.sortOrder === 'desc' ? 'translate-x-5' : 'translate-x-0'"
          />
        </button>
        <span class="text-[13px] uppercase tracking-widest text-[#4A5568]">Sort: {{ activeFilters.sortOrder === 'asc' ? 'Auf' : 'Ab' }}</span>
      </div>
    </div>
  </div>
</template>

<style scoped>
/* Reset some styles from base components to allow wrapping */
::v-deep(.overflow-x-auto) {
  overflow-x: visible !important;
}

::v-deep(.flex.gap-8) {
  flex-wrap: wrap !important;
  gap: 1rem !important;
  height: auto !important;
  padding-bottom: 0 !important;
}

::v-deep(.min-w-fit) {
  min-width: 0 !important;
}

/* Adjust size for mobile grid */
::v-deep(.w-16), ::v-deep(.h-16), ::v-deep(.w-20), ::v-deep(.h-20) {
  width: 3.5rem !important;
  height: 3.5rem !important;
}

::v-deep .text-sm,
::v-deep .text-xs,
::v-deep span {
  font-size: 10px !important;
}
</style>
